import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { classNames } from "../../helpers/classNames";

/**
 * The site menu (docs/design/HANDOFF.md, "Menu"). Mobile and tablet open a
 * panel from the right over a dark backdrop; desktop shows a "Menu" chip that
 * opens a dropdown. Help lives here, not in the header.
 *
 * `items`: [{ key, label, icon, to | href | onClick, badge, tone: "default" | "muted" | "quiet" }]
 *   A `divider: true` item draws a line.
 * `signedInAs`: the email shown at the bottom.
 */
const MenuButton = ({ items = [], signedInAs = null, label = "Menu" }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const firstItemRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    // Only the slide-in panel covers the page; the desktop dropdown doesn't.
    if (window.matchMedia && !window.matchMedia("(min-width: 1024px)").matches) {
      document.body.style.overflow = "hidden";
    }
    firstItemRef.current?.focus?.();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const close = () => setOpen(false);

  const renderItem = (item, index) => {
    if (item.divider) return <div key={`divider-${index}`} className="mx-3 my-2 h-px bg-md-pink-light-1" aria-hidden="true" />;
    const muted = item.tone === "muted";
    const quiet = item.tone === "quiet";
    const iconWrap = (
      <span
        aria-hidden="true"
        className={classNames(
          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full",
          quiet ? "" : muted ? "bg-[#F1ECE5] text-[#4A4446]" : "bg-md-pink/10 text-md-pink-dark"
        )}
      >
        {item.icon}
      </span>
    );
    const body = (
      <>
        {iconWrap}
        <span className="flex-1 text-left">{item.label}</span>
        {item.badge != null && item.badge !== 0 ? (
          <span className="flex h-[26px] min-w-[26px] items-center justify-center rounded-full bg-md-pink-dark px-2 text-[13px] font-bold text-white">
            {item.badge}
          </span>
        ) : null}
      </>
    );
    const classes = classNames(
      "flex min-h-[52px] w-full items-center gap-3.5 rounded-xl px-3 text-base font-semibold no-underline",
      "hover:bg-md-pink-light-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink",
      quiet ? "text-[#4A4446]" : "text-md-dark"
    );
    const ref = index === 0 ? firstItemRef : undefined;
    const onClick = () => {
      close();
      item.onClick?.();
    };
    if (item.to) {
      return (
        <Link key={item.key} ref={ref} to={item.to} className={classes} onClick={onClick}>
          {body}
        </Link>
      );
    }
    if (item.href) {
      return (
        <a key={item.key} ref={ref} href={item.href} className={classes} onClick={onClick}>
          {body}
        </a>
      );
    }
    return (
      <button key={item.key} ref={ref} type="button" className={classes} onClick={onClick}>
        {body}
      </button>
    );
  };

  const panel = (
    <nav
      id={panelId}
      aria-label="Main menu"
      className={classNames(
        "flex flex-col gap-1 bg-white p-3 pb-5 shadow-[0_20px_50px_rgba(27,13,18,0.25)]",
        // Slide-in panel below lg, dropdown at lg.
        "fixed inset-y-0 right-0 z-[61] w-[300px] max-w-[85%] rounded-l-[20px] md:w-[360px]",
        "lg:absolute lg:inset-auto lg:right-0 lg:top-[calc(100%+8px)] lg:w-[300px] lg:max-w-none lg:rounded-[18px]"
      )}
    >
      <div className="flex items-center justify-between pb-2 pl-3">
        <span className="text-lg font-bold text-md-dark">{label}</span>
        <button
          type="button"
          aria-label="Close menu"
          onClick={close}
          className="flex h-11 w-11 items-center justify-center rounded-md text-md-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
        >
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      {items.map(renderItem)}
      {signedInAs ? <div className="mt-auto px-3 pt-3 text-[13px] text-md-muted lg:mt-2">Signed in as {signedInAs}</div> : null}
    </nav>
  );

  return (
    <div className="relative">
      {/* Mobile and tablet trigger: icon only. */}
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-11 items-center justify-center rounded-md text-md-dark lg:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
      >
        <MenuIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      {/* Desktop trigger: outlined chip with the word. */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="hidden min-h-[44px] items-center gap-2 rounded-full border border-md-control bg-white px-4 text-[15px] font-semibold text-md-dark lg:flex hover:border-md-control-input focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
      >
        <MenuIcon className="h-5 w-5" aria-hidden="true" />
        {label}
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={close}
            className="fixed inset-0 z-[60] cursor-default bg-[rgba(27,13,18,0.5)] lg:bg-[rgba(27,13,18,0.12)]"
          />
          {panel}
        </>
      ) : null}
    </div>
  );
};

export default MenuButton;
